import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bot, Send, Upload, RefreshCw, User, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTED_QUESTIONS = [
  "What is the average cash price for a knee replacement?",
  "How do I know if cash pay is better than using my insurance?",
  "What is a CPT code and why does it matter?",
  "I need back surgery — what procedures might I need?",
  "How much can I save by traveling to Oklahoma City for surgery?",
];

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'} mb-4`}>
      <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 ${isUser ? 'bg-stone-200' : 'bg-[#0D3D4A]'}`}>
        {isUser ? <User size={15} className="text-stone-600" /> : <Bot size={15} className="text-white" />}
      </div>
      <div className={`max-w-[75%] px-4 py-3 text-sm leading-relaxed ${
        isUser ? 'bg-[#0D3D4A] text-white' : 'bg-white border border-stone-200 text-stone-700'
      }`}>
        <p className="whitespace-pre-wrap">{msg.content}</p>
      </div>
    </div>
  );
}

export default function AIAssistantPage() {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm Meridian AI, powered by Claude Sonnet. I'm here to help you navigate surgical costs, understand your procedure options, and find the best cash-pay providers for your needs.\n\nYou can ask me anything about procedures, CPT codes, cost comparisons, or even upload medical documents for me to analyze. How can I help you today?" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const { authHeaders } = useAuth();
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    const userText = text || input.trim();
    if (!userText) return;

    setMessages(prev => [...prev, { role: 'user', content: userText }]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ai/chat`, {
        message: userText,
        session_id: sessionId
      }, { headers: authHeaders });

      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'I apologize, I encountered an error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    setUploadLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    setMessages(prev => [...prev, { role: 'user', content: `[Uploaded document: ${file.name}]` }]);
    try {
      const res = await axios.post(`${API}/ai/analyze-document`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' }
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.analysis }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'I had trouble analyzing that document. Please ensure it\'s a clear image (JPG, PNG, WEBP) or PDF.' }]);
    } finally {
      setUploadLoading(false);
      setUploadFile(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) handleUpload(file);
  };

  const handleReset = () => {
    setMessages([{
      role: 'assistant',
      content: "Hi! I'm Meridian AI. How can I help you with surgical pricing and procedures today?"
    }]);
    setSessionId(null);
    setInput('');
  };

  return (
    <div className="min-h-screen bg-[#F0EEE9] flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 py-6 px-6 md:px-12">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#0D3D4A] flex items-center justify-center">
              <Bot size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-stone-900" style={{fontFamily: 'Outfit, sans-serif'}}>Meridian AI Assistant</h1>
              <p className="text-xs text-stone-500">Powered by Claude Sonnet 4.5 &bull; Medical cost guidance</p>
            </div>
          </div>
          <button
            onClick={handleReset}
            className="text-[#A8B4B8] hover:text-stone-700 flex items-center gap-1.5 text-sm"
            data-testid="reset-chat-btn"
          >
            <RefreshCw size={14} /> New Chat
          </button>
        </div>
      </div>

      <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full px-4 md:px-6 py-6 gap-4">
        {/* Suggested Questions (only at start) */}
        {messages.length === 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {SUGGESTED_QUESTIONS.map((q, i) => (
              <button
                key={i}
                onClick={() => sendMessage(q)}
                className="text-left p-3 bg-white border border-stone-200 text-sm text-stone-700 hover:border-[#0D3D4A] hover:bg-[#F0EEE9] transition-colors"
                data-testid={`suggested-question-${i}`}
              >
                {q}
              </button>
            ))}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 space-y-2" data-testid="chat-messages">
          {messages.map((msg, i) => (
            <MessageBubble key={i} msg={msg} />
          ))}
          {(loading || uploadLoading) && (
            <div className="flex gap-3 mb-4">
              <div className="w-8 h-8 bg-[#0D3D4A] flex items-center justify-center flex-shrink-0">
                <Bot size={15} className="text-white" />
              </div>
              <div className="bg-white border border-stone-200 px-4 py-3">
                <div className="flex gap-1 items-center h-5">
                  {[0,1,2].map(i => (
                    <div key={i} className="w-2 h-2 bg-stone-300 rounded-full animate-bounce" style={{animationDelay: `${i * 0.15}s`}} />
                  ))}
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="bg-white border border-[#A8B4B8]">
          <div className="flex items-end gap-2 p-3">
            <button
              onClick={() => document.getElementById('ai-file-upload').click()}
              className="p-2 text-[#A8B4B8] hover:text-[#0D3D4A] transition-colors flex-shrink-0"
              title="Upload medical document"
              data-testid="ai-upload-btn"
            >
              <Upload size={18} />
              <input
                id="ai-file-upload"
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.pdf"
                onChange={handleFileChange}
                className="hidden"
              />
            </button>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
              placeholder="Ask about procedures, CPT codes, costs, or upload a medical document..."
              rows={2}
              className="flex-1 resize-none text-sm text-stone-900 bg-transparent focus:outline-none placeholder:text-[#A8B4B8]"
              data-testid="ai-chat-input"
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              className="p-2 bg-[#0D3D4A] text-white hover:bg-[#0E4557] transition-colors disabled:opacity-40 flex-shrink-0"
              data-testid="ai-send-btn"
            >
              <Send size={16} />
            </button>
          </div>
          <div className="px-4 pb-2">
            <p className="text-xs text-[#A8B4B8]">Press Enter to send &bull; Shift+Enter for new line &bull; Upload medical documents with the paperclip icon</p>
          </div>
        </div>
      </div>
    </div>
  );
}
